/**
 * QuestionnaireDetail — /w/:slug/app/questionnaires/:id.
 *
 * P8 / M4 review view + M5 review loop + PR-P2-4 Distribute. Renders
 * every extracted question with its LLM draft, citations, and confidence
 * badge; lets the preparer inline-edit `preparer_text`, approve or
 * request changes on each answer, assign questions to teammates, and
 * approve the whole questionnaire once every answer is approved.
 *
 * QuestionRow (design-system) throws when the tri-role attribution
 * (author/sourceReviewer/approver) is incomplete. This page instead uses
 * an inline `DraftAnswerCard` that tracks the review lifecycle explicitly
 * and only graduates to QuestionRow once M6/export lands (Contract C7
 * enforcement happens at that render).
 *
 * PR-P2-4 "Distribute" scope trims vs the uc-5-questionnaires mockup's
 * design-rationale (documented in docs/implementation-plan.md §3.4
 * deviation notes):
 *   - No category-driven suggested-assignee routing ("technical →
 *     Marcus, legal → Priya") — our schema has no business-category
 *     taxonomy for questions (question_type is a form-control kind:
 *     yes_no/short/long/…, not a domain). Manual assignee picker only.
 *   - Filters are All / Assigned to me / Unassigned / Needs review —
 *     not the mockup's Blocked/Ready-for-review/Approved set; "blocked"
 *     isn't a real state in this schema.
 *   - No two-column right-rail layout — Distribute is a toolbar above
 *     the list, matching this page's existing single-column section
 *     language (Export section below).
 *
 * ux-adversary findings fixed in this PR (Priya + Nadia persona critics,
 * aesthetic + flow critics — run in critique-only mode against the built
 * page, no rendered mockup existed for Distribute):
 *   - Blocker (Priya): approveAnswer's require_approval_rationale gate
 *     was discoverable only by failing Approve — on an N-question
 *     questionnaire that's N failed round-trips. Fixed: the workspace
 *     setting now rides on GET /:id (`requireApprovalRationale`) and the
 *     rationale field renders proactively when it's on.
 *   - Blocker (flow): saveEdit/submitRequestChanges unconditionally
 *     collapsed back to view mode even when the mutation failed,
 *     silently discarding the reviewer's typed text — the exact
 *     invisible-error class already paid down once in QuestionnaireNew.tsx.
 *     Fixed: every mutation wrapper now returns a typed result the card
 *     checks before leaving edit/request-changes mode.
 *   - Blocker (flow, backend): `editAnswer` had no `approved_at IS NULL`
 *     guard, unlike `approveAnswer` — a reviewer with a stale Edit panel
 *     open could silently overwrite an answer a teammate had just
 *     approved. Fixed in questionnaire-review-service.ts.
 *   - Blocker/High (Nadia + Priya + aesthetic, 3 independent critics):
 *     `<details open>` was a static literal with no state — every card
 *     rendered permanently expanded, and any manual collapse a reviewer
 *     made was destroyed by the next refetch. Fixed: open/collapsed
 *     state is tracked per question (defaults to open only for
 *     unapproved questions), with an Expand all/Collapse all toggle.
 *   - High (aesthetic + flow, 2 critics): the citation `<dialog>` was
 *     opened via a static `open` attribute, never `.showModal()` — no
 *     backdrop, no focus trap, no Escape-to-close, and it rendered
 *     in-flow rather than as a popover. Fixed with a ref + showModal()/
 *     close().
 *   - High (Priya): citation chips showed only risk_item_id + relevance
 *     + why, forcing a click-through to judge every citation. Fixed:
 *     GET /:id now resolves cited register-item titles in one extra
 *     query (same pattern the export renderer already uses).
 *   - High (Priya + aesthetic, 2 critics): Export section surfaced the
 *     internal "Contract C7" identifier directly to the end user. Fixed:
 *     plain-language copy, no internal contract name.
 *   - High (Priya): Approve was the sole `variant="primary"` action
 *     against 3 secondary buttons, biasing toward rubber-stamping
 *     instead of reading. Fixed: Approve and Request changes render
 *     with equal visual weight.
 *   - High (Priya): low and medium confidence both rendered the same
 *     neutral badge tone — the riskiest drafts were visually camouflaged.
 *     Fixed: a 3-tone confidence indicator (danger/warning/success).
 *   - High (flow): a single page-level `busy` boolean froze every
 *     control on every question whenever any one mutation was in
 *     flight. Fixed: busy state is now tracked per question.
 *   - High (flow): changing FilterBar while a card was mid-edit
 *     unmounted it, silently destroying the in-progress local edit.
 *     Fixed: a card with unsaved changes stays visible regardless of
 *     the active filter.
 *   - High (Nadia, 2 findings): Distribute could only bulk-assign the
 *     unassigned pool to one owner — no way to split questions across
 *     several owners or reassign from someone who's out. Fixed: a
 *     checkbox-driven multi-select toolbar assigns any chosen subset
 *     (assigned or not) to one owner; the single-click "assign all
 *     unassigned" action is kept alongside it as the common-case
 *     shortcut.
 *   - Medium (aesthetic): `--radius-sm` doesn't exist in the design
 *     system (resolves to 0 — square corners); `--radius-pill` and
 *     `--accent-subtle` were invented inline with fallbacks. Fixed:
 *     swapped for confirmed-existing tokens (`--radius-input`,
 *     `--radius-chip`, `--surface-elevated`) — see CLAUDE.md's
 *     "Design-token fidelity" section on why an unconfirmed token with
 *     a fallback is treated as a bug, not a style choice.
 *   - Medium (Priya): approved answers showed a bare date with no
 *     approver name, and edits showed no editor name. Fixed: GET /:id
 *     resolves preparer/approver display names alongside assignees.
 *   - Medium (flow): the assignee avatar badge was `aria-hidden` with
 *     only a hover `title` — invisible to keyboard/touch/screen-reader
 *     users. Fixed: real `aria-label`, no `aria-hidden`.
 *   - Medium (flow): a failed mutation's error only reached a banner at
 *     the very top of the page, easy to miss on question 30 of 40.
 *     Fixed: the banner scrolls into view and takes focus on error.
 *   - Nit (Nadia): Distribute was gated on `editable` (in_review/
 *     drafting only), blocking assignment while a questionnaire is
 *     still in `matching` even though questions already exist by then.
 *     Fixed: a separate `canAssign` gate covers matching too.
 *   - Nit (flow): the bulk-assign assignee picker didn't reset after a
 *     successful assign. Fixed.
 *
 * Deferred (documented in docs/implementation-plan.md Appendix F, not
 * silently dropped): true cross-workspace "my work" aggregation (Nadia
 * Blocker — MyWork.tsx is workspace-scoped app-wide, a pre-existing
 * pattern this PR extends rather than introduces; fixing it is an
 * app-wide session/routing change outside one PR's file scope);
 * composable multi-axis filters; a per-assignee workload count strip;
 * an urgency sub-count on MyWork's rollup chips; extracting the
 * hand-rolled FilterBar/select/avatar chrome into shared DS components
 * (C17 debt — matches the project's existing calibration-state
 * convention of tracking, not blocking, this class of issue).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import jsPDF from 'jspdf';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Button,
  PageHeader,
  Spinner,
  Textarea,
} from '@aigovops/design-system';
import type {
  AnswerCitation,
  JsonExportPayload,
  QuestionType,
  QuestionnaireStatus,
} from '@aigovops/core';
import { useWorkspace } from '../lib/workspace';
import { useAuth } from '../lib/auth';
import { useWorkspaceQuery } from '../hooks/useWorkspaceQuery';
import { queryKeys } from '../lib/query-keys';
import { renderQuestionnairePdf } from '../lib/questionnaire-pdf';
import {
  approveAnswer as apiApproveAnswer,
  approveQuestionnaire as apiApproveQuestionnaire,
  assignQuestion,
  bulkAssignQuestions,
  editAnswer as apiEditAnswer,
  getQuestionnaire,
  listWorkspaceMembers,
  req,
  requestAnswerChanges,
  type QuestionnaireAssigneeInfo,
  type QuestionnaireCitedItem,
  type WorkspaceMember,
} from '../lib/api';

type DraftOrigin = 'attested' | 'declared' | 'ai_inferred';

interface QuestionRow {
  question_id: string;
  seq_no: number;
  section: string | null;
  question_type: QuestionType;
  text: string;
  extracted_via: 'llm' | 'recognizer' | 'template_slot';
  draft_origin: DraftOrigin | null;
  assigned_to_user_id: string | null;
}

interface AnswerRow {
  answer_id: string;
  question_id: string;
  draft_text: string | null;
  draft_confidence: 'high' | 'medium' | 'low' | null;
  drafted_by: 'llm' | 'template_slot' | null;
  citations_json: string;
  preparer_text: string | null;
  preparer_edited_at: string | null;
  preparer_edited_by_user_id: string | null;
  approved_at: string | null;
  approved_by_user_id: string | null;
  needs_human_review: number;
  approval_note: string | null;
  change_request_reason: string | null;
  updated_at: string;
}

type Filter = 'all' | 'mine' | 'unassigned' | 'needs_review';

/** Result shape every mutation wrapper returns so a card can tell
 *  success from failure — and show the REAL reason, not a guess. */
type ActionResult = { ok: true } | { ok: false; message: string };

const STATUS_LABEL: Record<QuestionnaireStatus, string> = {
  extracting: 'Extracting',
  matching: 'Matching',
  drafting: 'Drafting',
  in_review: 'In review',
  approved: 'Approved',
  exported: 'Exported',
};

function toResult(promise: Promise<unknown>): Promise<ActionResult> {
  return promise.then(
    () => ({ ok: true }) as const,
    (err) => ({ ok: false, message: err instanceof Error ? err.message : String(err) }) as const
  );
}

export function QuestionnaireDetail() {
  const { workspace, role } = useWorkspace();
  const { user } = useAuth();
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const slug = workspace.slug;
  const listHref = `/w/${slug}/app/questionnaires`;
  const canManage = role === 'owner' || role === 'admin' || role === 'editor';

  const { data: stateData, error: stateError, loading: stateLoading, refetch: stateRefetch } = useWorkspaceQuery(
    (slug) => queryKeys.questionnaireDetail(slug, id ?? ''),
    (slug) => getQuestionnaire(slug, id!),
    { enabled: !!id },
  );
  const state = { data: stateData, error: stateError, loading: stateLoading, refetch: stateRefetch };
  const [pageBusy, setPageBusy] = useState(false);
  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());
  const [banner, setBanner] = useState<{ variant: 'success' | 'error' | 'info'; text: string } | null>(null);
  const bannerRef = useRef<HTMLDivElement>(null);
  const [members, setMembers] = useState<WorkspaceMember[] | null>(null);
  const [filter, setFilter] = useState<Filter>(searchParams.get('assigned') === 'me' ? 'mine' : 'all');
  const [dirtyIds, setDirtyIds] = useState<Set<string>>(new Set());
  const [openIds, setOpenIds] = useState<Set<string>>(new Set());
  const initializedOpenRef = useRef(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkAssignee, setBulkAssignee] = useState('');

  useEffect(() => {
    if (!canManage) return;
    let cancelled = false;
    listWorkspaceMembers(slug)
      .then((res) => {
        if (!cancelled) setMembers(res.members);
      })
      .catch(() => {
        /* silent — assignee picker just won't render options */
      });
    return () => {
      cancelled = true;
    };
  }, [slug, canManage]);

  // Default open/collapsed state, set once from the first successful
  // load (not re-derived on every refetch, which would blow away a
  // reviewer's manual expand/collapse choices — Nadia + Priya + the
  // aesthetic critic all independently caught the old static-`open`
  // version of this bug).
  useEffect(() => {
    if (initializedOpenRef.current || !state.data) return;
    initializedOpenRef.current = true;
    const approvedQ = new Set(state.data.answers.filter((a) => a.approved_at).map((a) => a.question_id));
    setOpenIds(new Set(state.data.questions.filter((q) => !approvedQ.has(q.question_id)).map((q) => q.question_id)));
  }, [state.data]);

  useEffect(() => {
    if (banner?.variant === 'error') {
      bannerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      bannerRef.current?.focus();
    }
  }, [banner]);

  const usersById = useMemo(() => {
    const m = new Map<string, QuestionnaireAssigneeInfo>();
    for (const u of state.data?.users ?? []) m.set(u.user_id, u);
    return m;
  }, [state.data?.users]);

  const citedTitleById = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of state.data?.citedItems ?? ([] as QuestionnaireCitedItem[])) m.set(c.item_id, c.title);
    return m;
  }, [state.data?.citedItems]);

  const withRowBusy = useCallback(async (rowId: string, action: () => Promise<ActionResult>): Promise<ActionResult> => {
    setBusyIds((prev) => new Set(prev).add(rowId));
    setBanner(null);
    try {
      const result = await action();
      if (!result.ok) setBanner({ variant: 'error', text: result.message });
      else state.refetch();
      return result;
    } finally {
      setBusyIds((prev) => {
        const next = new Set(prev);
        next.delete(rowId);
        return next;
      });
    }
  }, [state]);

  const patchAnswer = useCallback(
    (questionId: string, answerId: string, text: string) =>
      withRowBusy(questionId, () => toResult(apiEditAnswer(slug, id!, answerId, text))),
    [id, slug, withRowBusy]
  );

  const approveAnswer = useCallback(
    (questionId: string, answerId: string, approvalNote?: string) =>
      withRowBusy(questionId, () => toResult(apiApproveAnswer(slug, id!, answerId, approvalNote))),
    [id, slug, withRowBusy]
  );

  const requestChanges = useCallback(
    (questionId: string, answerId: string, reason: string) =>
      withRowBusy(questionId, () => toResult(requestAnswerChanges(slug, id!, answerId, reason))),
    [id, slug, withRowBusy]
  );

  const assignOne = useCallback(
    (questionId: string, assigneeUserId: string | null) =>
      withRowBusy(questionId, () => toResult(assignQuestion(slug, id!, questionId, assigneeUserId))),
    [id, slug, withRowBusy]
  );

  const setDirty = useCallback((questionId: string, dirty: boolean) => {
    setDirtyIds((prev) => {
      if (dirty === prev.has(questionId)) return prev;
      const next = new Set(prev);
      if (dirty) next.add(questionId);
      else next.delete(questionId);
      return next;
    });
  }, []);

  const toggleOpen = useCallback((questionId: string, isOpen: boolean) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (isOpen) next.add(questionId);
      else next.delete(questionId);
      return next;
    });
  }, []);

  const toggleSelected = useCallback((questionId: string, isSelected: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (isSelected) next.add(questionId);
      else next.delete(questionId);
      return next;
    });
  }, []);

  const questions: QuestionRow[] = state.data?.questions ?? [];
  const answered: AnswerRow[] = state.data?.answers ?? [];
  const answerByQ = useMemo(() => new Map(answered.map((a) => [a.question_id, a])), [answered]);
  const approvedCount = answered.filter((a) => a.approved_at).length;
  const totalCount = questions.length;
  const canApproveAll =
    state.data?.questionnaire.status === 'in_review' && totalCount > 0 && approvedCount === totalCount;
  const canExport =
    state.data?.questionnaire.status === 'approved' || state.data?.questionnaire.status === 'exported';
  const editable =
    state.data?.questionnaire.status === 'in_review' || state.data?.questionnaire.status === 'drafting';
  // Distribute is useful earlier than full edit/approve — questions
  // already exist once extraction finishes, before drafting starts
  // (Nadia nit: the old code reused `editable`, blocking assignment
  // during 'matching' with zero reason to).
  const canAssign =
    canManage &&
    (state.data?.questionnaire.status === 'matching' ||
      state.data?.questionnaire.status === 'drafting' ||
      state.data?.questionnaire.status === 'in_review');

  const unassigned = questions.filter((q) => !q.assigned_to_user_id);
  const filterCounts = {
    all: questions.length,
    mine: questions.filter((q) => q.assigned_to_user_id === user?.id).length,
    unassigned: unassigned.length,
    needs_review: questions.filter((q) => {
      const a = answerByQ.get(q.question_id);
      return a?.needs_human_review === 1 && !a.approved_at;
    }).length,
  };
  const passesFilter = useCallback(
    (q: QuestionRow) => {
      if (filter === 'mine') return q.assigned_to_user_id === user?.id;
      if (filter === 'unassigned') return !q.assigned_to_user_id;
      if (filter === 'needs_review') {
        const a = answerByQ.get(q.question_id);
        return a?.needs_human_review === 1 && !a.approved_at;
      }
      return true;
    },
    [filter, user?.id, answerByQ]
  );
  // A card mid-edit stays visible even if the active filter would
  // otherwise hide it — changing filters used to unmount (and silently
  // discard) an in-progress edit (flow critic High finding).
  const visibleQuestions = questions.filter((q) => passesFilter(q) || dirtyIds.has(q.question_id));

  const changeFilter = (next: Filter) => {
    setFilter(next);
    const params = new URLSearchParams(searchParams);
    if (next === 'mine') params.set('assigned', 'me');
    else params.delete('assigned');
    setSearchParams(params, { replace: true });
  };

  const bulkAssign = useCallback(
    async (questionIds: string[], assigneeUserId: string) => {
      if (!id || questionIds.length === 0) return;
      setPageBusy(true);
      setBanner(null);
      try {
        const result = await bulkAssignQuestions(slug, id, questionIds, assigneeUserId);
        if (result.refused.length > 0) {
          const bySeq = new Map(questions.map((q) => [q.question_id, q.seq_no]));
          const refusedLabels = result.refused
            .slice(0, 5)
            .map((r) => `Q${String(bySeq.get(r.question_id) ?? '?').padStart(2, '0')}`)
            .join(', ');
          const more = result.refused.length > 5 ? ` +${result.refused.length - 5} more` : '';
          setBanner({
            variant: 'error',
            text: `Assigned ${result.assigned.length} of ${questionIds.length} — refused: ${refusedLabels}${more} (assignee may no longer be an active member).`,
          });
        } else {
          setBanner({ variant: 'success', text: `Assigned ${result.assigned.length} question(s).` });
        }
        setBulkAssignee('');
        setSelectedIds(new Set());
        state.refetch();
      } catch (err) {
        setBanner({ variant: 'error', text: err instanceof Error ? err.message : String(err) });
      } finally {
        setPageBusy(false);
      }
    },
    [id, slug, state, questions]
  );

  const approveQuestionnaire = useCallback(async () => {
    if (!id) return;
    setPageBusy(true);
    setBanner(null);
    try {
      await apiApproveQuestionnaire(slug, id);
      setBanner({ variant: 'success', text: 'Questionnaire approved.' });
      state.refetch();
    } catch (err) {
      setBanner({ variant: 'error', text: err instanceof Error ? err.message : String(err) });
    } finally {
      setPageBusy(false);
    }
  }, [id, slug, state]);

  const downloadExport = useCallback(
    async (format: 'json' | 'xlsx' | 'pdf') => {
      if (!id) return;
      setPageBusy(true);
      setBanner(null);
      try {
        if (format === 'pdf') {
          const payload = await req<JsonExportPayload>(
            `/api/w/${encodeURIComponent(slug)}/questionnaires/${encodeURIComponent(id)}/export?format=json`
          );
          const doc = new jsPDF({ unit: 'pt', format: 'letter' });
          renderQuestionnairePdf(doc, payload);
          doc.save(safeName(payload.questionnaire.title, id) + '.pdf');
        } else {
          const res = await fetch( // raw-fetch-ok: binary download (DOCX/MD)
            `/api/w/${encodeURIComponent(slug)}/questionnaires/${encodeURIComponent(id)}/export?format=${format}`,
            { credentials: 'include' }
          );
          if (!res.ok) {
            const b = (await res.json().catch(() => null)) as { detail?: string; error?: string } | null;
            throw new Error(b?.detail ?? b?.error ?? `HTTP ${res.status}`);
          }
          const blob =
            format === 'json'
              ? new Blob([JSON.stringify(await res.json(), null, 2)], { type: 'application/json' })
              : await res.blob();
          triggerDownload(blob, safeName(state.data?.questionnaire.title ?? id, id) + '.' + format);
        }
        // XLSX export flips status to 'exported' server-side; refetch to reflect.
        state.refetch();
      } catch (err) {
        setBanner({ variant: 'error', text: err instanceof Error ? err.message : String(err) });
      } finally {
        setPageBusy(false);
      }
    },
    [id, slug, state]
  );

  const allVisibleSelected = visibleQuestions.length > 0 && visibleQuestions.every((q) => selectedIds.has(q.question_id));

  return (
    <div style={{ display: 'grid', gap: 'var(--space-6)' }}>
      {state.loading ? <Spinner /> : null}
      {state.error ? <Alert variant="error">{state.error}</Alert> : null}
      {banner ? (
        <div ref={bannerRef} tabIndex={-1}>
          <Alert variant={banner.variant}>{banner.text}</Alert>
        </div>
      ) : null}
      {state.data ? (
        <>
          <PageHeader
            title={state.data.questionnaire.title}
            subtitle={
              <>
                {workspace.name} · {STATUS_LABEL[state.data.questionnaire.status]} · {approvedCount} of {totalCount}{' '}
                approved · {answered.filter((a) => a.needs_human_review).length} need human review
              </>
            }
            right={
              <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
                <Link to={listHref} style={{ textDecoration: 'none' }}>
                  <Button variant="secondary" disabled={pageBusy}>← All questionnaires</Button>
                </Link>
                {state.data.questionnaire.status === 'in_review' ? (
                  <Button
                    variant="primary"
                    onClick={approveQuestionnaire}
                    disabled={pageBusy || !canApproveAll}
                    title={canApproveAll ? '' : 'Approve every answer first.'}
                  >
                    {pageBusy ? 'Working…' : 'Approve questionnaire →'}
                  </Button>
                ) : null}
              </div>
            }
          />

          <EvidenceReadinessSummary questions={questions} />

          {canAssign && questions.length > 0 ? (
            <DistributeToolbar
              unassignedCount={unassigned.length}
              selectedCount={selectedIds.size}
              visibleCount={visibleQuestions.length}
              allVisibleSelected={allVisibleSelected}
              members={members}
              busy={pageBusy}
              selectedAssignee={bulkAssignee}
              onSelectedAssigneeChange={setBulkAssignee}
              onToggleSelectAllVisible={() =>
                setSelectedIds(allVisibleSelected ? new Set() : new Set(visibleQuestions.map((q) => q.question_id)))
              }
              onAssignUnassigned={(assigneeUserId) => bulkAssign(unassigned.map((q) => q.question_id), assigneeUserId)}
              onAssignSelected={(assigneeUserId) => bulkAssign([...selectedIds], assigneeUserId)}
              onClearSelection={() => setSelectedIds(new Set())}
            />
          ) : null}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
            <FilterBar filter={filter} counts={filterCounts} onChange={changeFilter} />
            {questions.length > 0 ? (
              <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                <Button variant="secondary" compact onClick={() => setOpenIds(new Set(questions.map((q) => q.question_id)))}>
                  Expand all
                </Button>
                <Button variant="secondary" compact onClick={() => setOpenIds(new Set())}>
                  Collapse all
                </Button>
              </div>
            ) : null}
          </div>

          <DraftList
            questions={visibleQuestions}
            answers={answered}
            busyIds={busyIds}
            editable={editable}
            canAssign={canAssign}
            currentUserId={user?.id ?? null}
            members={members}
            usersById={usersById}
            citedTitleById={citedTitleById}
            registerPrefix={`/w/${slug}/app/register`}
            openIds={openIds}
            selectedIds={selectedIds}
            requireApprovalRationale={state.data.requireApprovalRationale}
            onEdit={patchAnswer}
            onApprove={approveAnswer}
            onRequestChanges={requestChanges}
            onAssign={assignOne}
            onDirtyChange={setDirty}
            onToggleOpen={toggleOpen}
            onToggleSelected={toggleSelected}
          />
          {visibleQuestions.length === 0 && questions.length > 0 ? (
            <Alert variant="info">No questions match this filter.</Alert>
          ) : null}
          {canExport ? (
            <section
              aria-labelledby="export-heading"
              style={{
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-card)',
                background: 'var(--surface)',
                padding: 'var(--space-4)',
                display: 'grid',
                gap: 'var(--space-3)',
                fontFamily: 'var(--font-ui)',
              }}
            >
              <h2 id="export-heading" style={{ margin: 0, fontSize: 'var(--font-size-md)', color: 'var(--text)' }}>
                Export
              </h2>
              <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: 'var(--font-size-2xs)' }}>
                You edited or approved an answer in this questionnaire — ask a second reviewer to export from their
                own session for a properly separated-duties deliverable.
              </p>
              <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
                <Button variant="secondary" onClick={() => downloadExport('pdf')} disabled={pageBusy}>
                  Download PDF
                </Button>
                <Button variant="secondary" onClick={() => downloadExport('xlsx')} disabled={pageBusy}>
                  Download XLSX
                </Button>
                <Button variant="secondary" onClick={() => downloadExport('json')} disabled={pageBusy}>
                  Download JSON
                </Button>
              </div>
            </section>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

// ---- Filter bar (PR-P2-4) -----------------------------------------------

function FilterBar({
  filter,
  counts,
  onChange,
}: {
  filter: Filter;
  counts: Record<Filter, number>;
  onChange: (f: Filter) => void;
}) {
  const options: Array<{ key: Filter; label: string }> = [
    { key: 'all', label: 'All' },
    { key: 'mine', label: 'Assigned to me' },
    { key: 'unassigned', label: 'Unassigned' },
    { key: 'needs_review', label: 'Needs review' },
  ];
  return (
    <div role="group" aria-label="Filter questions" style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
      {options.map((o) => {
        const active = filter === o.key;
        return (
          <button
            key={o.key}
            type="button"
            onClick={() => onChange(o.key)}
            aria-pressed={active}
            style={{
              padding: 'var(--space-1) var(--space-3)',
              borderRadius: 'var(--radius-chip)',
              border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
              background: active ? 'var(--surface-elevated)' : 'var(--surface)',
              color: active ? 'var(--accent)' : 'var(--text-muted)',
              fontSize: 'var(--font-size-3xs)',
              fontWeight: active ? 600 : 500,
              cursor: 'pointer',
              fontFamily: 'var(--font-ui)',
            }}
          >
            {o.label} · {counts[o.key]}
          </button>
        );
      })}
    </div>
  );
}

// ---- Distribute toolbar (PR-P2-4) ---------------------------------------
//
// Two actions sharing one assignee picker: the common-case "assign every
// unassigned question to one owner" shortcut, and a checkbox-driven
// multi-select that assigns (or reassigns) any chosen subset — covers
// Nadia's real job ("split 40 questions three ways", "reassign Priya's
// 8 questions while she's out") that the unassigned-only version couldn't.

function DistributeToolbar({
  unassignedCount,
  selectedCount,
  visibleCount,
  allVisibleSelected,
  members,
  busy,
  selectedAssignee,
  onSelectedAssigneeChange,
  onToggleSelectAllVisible,
  onAssignUnassigned,
  onAssignSelected,
  onClearSelection,
}: {
  unassignedCount: number;
  selectedCount: number;
  visibleCount: number;
  allVisibleSelected: boolean;
  members: WorkspaceMember[] | null;
  busy: boolean;
  selectedAssignee: string;
  onSelectedAssigneeChange: (userId: string) => void;
  onToggleSelectAllVisible: () => void;
  onAssignUnassigned: (assigneeUserId: string) => void;
  onAssignSelected: (assigneeUserId: string) => void;
  onClearSelection: () => void;
}) {
  return (
    <section
      aria-labelledby="distribute-heading"
      style={{
        border: '1px solid var(--border-strong)',
        borderRadius: 'var(--radius-card)',
        background: 'var(--surface)',
        padding: 'var(--space-4)',
        display: 'flex',
        gap: 'var(--space-3)',
        alignItems: 'center',
        flexWrap: 'wrap',
        fontFamily: 'var(--font-ui)',
      }}
    >
      <div style={{ minWidth: 160 }}>
        <h2 id="distribute-heading" style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--text)' }}>
          Distribute
        </h2>
        {visibleCount > 0 ? (
          <label style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', marginTop: 'var(--space-1)', fontSize: 'var(--font-size-3xs)', color: 'var(--text-dim)' }}>
            <input type="checkbox" checked={allVisibleSelected} onChange={onToggleSelectAllVisible} />
            Select all visible ({visibleCount})
          </label>
        ) : null}
      </div>
      <select
        value={selectedAssignee}
        onChange={(e) => onSelectedAssigneeChange(e.target.value)}
        disabled={busy || members == null}
        aria-label="Assignee"
        style={{
          padding: 'var(--space-2)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-input)',
          fontSize: 'var(--font-size-2xs)',
          minWidth: 200,
          background: 'var(--surface)',
          color: 'var(--text)',
        }}
      >
        <option value="">— Choose a teammate —</option>
        {(members ?? []).map((m) => (
          <option key={m.user_id} value={m.user_id}>
            {m.display_name} ({m.email})
          </option>
        ))}
      </select>
      {selectedCount > 0 ? (
        <>
          <Button variant="secondary" compact onClick={() => onAssignSelected(selectedAssignee)} disabled={busy || !selectedAssignee}>
            Assign {selectedCount} selected →
          </Button>
          <Button variant="secondary" compact onClick={onClearSelection} disabled={busy}>
            Clear selection
          </Button>
        </>
      ) : (
        <Button
          variant="secondary"
          compact
          onClick={() => onAssignUnassigned(selectedAssignee)}
          disabled={busy || !selectedAssignee || unassignedCount === 0}
          title={unassignedCount === 0 ? 'No unassigned questions.' : ''}
        >
          Assign {unassignedCount} unassigned →
        </Button>
      )}
    </section>
  );
}

// ---- Draft list -----------------------------------------------------------

interface DraftListProps {
  questions: QuestionRow[];
  answers: AnswerRow[];
  busyIds: Set<string>;
  editable: boolean;
  canAssign: boolean;
  currentUserId: string | null;
  members: WorkspaceMember[] | null;
  usersById: Map<string, QuestionnaireAssigneeInfo>;
  citedTitleById: Map<string, string>;
  registerPrefix: string;
  openIds: Set<string>;
  selectedIds: Set<string>;
  requireApprovalRationale: boolean;
  onEdit: (questionId: string, answerId: string, text: string) => Promise<ActionResult>;
  onApprove: (questionId: string, answerId: string, approvalNote?: string) => Promise<ActionResult>;
  onRequestChanges: (questionId: string, answerId: string, reason: string) => Promise<ActionResult>;
  onAssign: (questionId: string, assigneeUserId: string | null) => Promise<ActionResult>;
  onDirtyChange: (questionId: string, dirty: boolean) => void;
  onToggleOpen: (questionId: string, open: boolean) => void;
  onToggleSelected: (questionId: string, selected: boolean) => void;
}

function DraftList({
  questions,
  answers,
  busyIds,
  editable,
  canAssign,
  currentUserId,
  members,
  usersById,
  citedTitleById,
  registerPrefix,
  openIds,
  selectedIds,
  requireApprovalRationale,
  onEdit,
  onApprove,
  onRequestChanges,
  onAssign,
  onDirtyChange,
  onToggleOpen,
  onToggleSelected,
}: DraftListProps) {
  const answerByQ = new Map(answers.map((a) => [a.question_id, a]));
  if (questions.length === 0) {
    return (
      <Alert variant="info">
        No questions extracted yet. Extraction may still be running, or the ingest didn't produce any recognizable questions.
      </Alert>
    );
  }
  return (
    <ol style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 'var(--space-3)' }}>
      {questions.map((q) => (
        <li key={q.question_id} style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'flex-start' }}>
          {canAssign ? (
            <input
              type="checkbox"
              aria-label={`Select Q${String(q.seq_no).padStart(2, '0')} for bulk assignment`}
              checked={selectedIds.has(q.question_id)}
              onChange={(e) => onToggleSelected(q.question_id, e.target.checked)}
              style={{ marginTop: 'var(--space-4)', flexShrink: 0 }}
            />
          ) : null}
          <div style={{ flex: 1, minWidth: 0 }}>
            <DraftAnswerCard
              question={q}
              answer={answerByQ.get(q.question_id) ?? null}
              busy={busyIds.has(q.question_id)}
              editable={editable}
              canAssign={canAssign}
              currentUserId={currentUserId}
              members={members}
              usersById={usersById}
              assignee={q.assigned_to_user_id ? (usersById.get(q.assigned_to_user_id) ?? null) : null}
              citedTitleById={citedTitleById}
              registerPrefix={registerPrefix}
              open={openIds.has(q.question_id)}
              requireApprovalRationale={requireApprovalRationale}
              onEdit={onEdit}
              onApprove={onApprove}
              onRequestChanges={onRequestChanges}
              onAssign={onAssign}
              onDirtyChange={onDirtyChange}
              onToggleOpen={onToggleOpen}
            />
          </div>
        </li>
      ))}
    </ol>
  );
}

// ---- Draft answer card -----------------------------------------------

interface DraftAnswerCardProps {
  question: QuestionRow;
  answer: AnswerRow | null;
  busy: boolean;
  editable: boolean;
  canAssign: boolean;
  currentUserId: string | null;
  members: WorkspaceMember[] | null;
  usersById: Map<string, QuestionnaireAssigneeInfo>;
  assignee: QuestionnaireAssigneeInfo | null;
  citedTitleById: Map<string, string>;
  registerPrefix: string;
  open: boolean;
  requireApprovalRationale: boolean;
  onEdit: (questionId: string, answerId: string, text: string) => Promise<ActionResult>;
  onApprove: (questionId: string, answerId: string, approvalNote?: string) => Promise<ActionResult>;
  onRequestChanges: (questionId: string, answerId: string, reason: string) => Promise<ActionResult>;
  onAssign: (questionId: string, assigneeUserId: string | null) => Promise<ActionResult>;
  onDirtyChange: (questionId: string, dirty: boolean) => void;
  onToggleOpen: (questionId: string, open: boolean) => void;
}

type CardMode = 'view' | 'editing' | 'requesting_changes';

function DraftAnswerCard({
  question,
  answer,
  busy,
  editable,
  canAssign,
  currentUserId,
  members,
  usersById,
  assignee,
  citedTitleById,
  registerPrefix,
  open,
  requireApprovalRationale,
  onEdit,
  onApprove,
  onRequestChanges,
  onAssign,
  onDirtyChange,
  onToggleOpen,
}: DraftAnswerCardProps) {
  const citations: AnswerCitation[] = answer ? parseCitations(answer.citations_json) : [];
  const confidence = answer?.draft_confidence ?? 'low';
  const needsReview = answer?.needs_human_review === 1;
  const isApproved = Boolean(answer?.approved_at);
  const displayText = answer?.preparer_text ?? answer?.draft_text ?? '';
  const [mode, setMode] = useState<CardMode>('view');
  const [draftEdit, setDraftEdit] = useState('');
  const [changeReason, setChangeReason] = useState('');
  const [approvalNoteDraft, setApprovalNoteDraft] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const isMine = currentUserId != null && question.assigned_to_user_id === currentUserId;
  // usersById comes from GET /:id (any workspace member who has ever
  // touched this answer, not just currently-active members) — deliberately
  // NOT `members` (canManage-gated, active-members-only), which would
  // leave viewers seeing nothing and would drop anyone who's since left
  // the workspace.
  const editedByName = answer?.preparer_edited_by_user_id
    ? (usersById.get(answer.preparer_edited_by_user_id)?.display_name ?? null)
    : null;
  const approvedByName = answer?.approved_by_user_id
    ? (usersById.get(answer.approved_by_user_id)?.display_name ?? null)
    : null;

  useEffect(() => {
    onDirtyChange(question.question_id, mode !== 'view');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const startEdit = () => {
    setDraftEdit(displayText);
    setActionError(null);
    setMode('editing');
  };

  const cancelEdit = () => {
    setActionError(null);
    setMode('view');
  };

  const saveEdit = async () => {
    if (!answer) return;
    const result = await onEdit(question.question_id, answer.answer_id, draftEdit);
    if (result.ok) setMode('view');
    else setActionError(result.message);
  };

  const submitApprove = async () => {
    if (!answer) return;
    setActionError(null);
    const result = await onApprove(question.question_id, answer.answer_id, approvalNoteDraft.trim() || undefined);
    if (!result.ok) setActionError(result.message);
  };

  const startRequestChanges = () => {
    setChangeReason('');
    setActionError(null);
    setMode('requesting_changes');
  };

  const submitRequestChanges = async () => {
    if (!answer || !changeReason.trim()) return;
    const result = await onRequestChanges(question.question_id, answer.answer_id, changeReason.trim());
    if (result.ok) setMode('view');
    else setActionError(result.message);
  };

  return (
    <details
      data-part="draft-answer"
      data-approved={isApproved || undefined}
      open={open}
      onToggle={(e) => onToggleOpen(question.question_id, e.currentTarget.open)}
      style={{
        border: `1px solid ${isApproved ? 'var(--success)' : 'var(--border)'}`,
        borderRadius: 'var(--radius-card)',
        background: 'var(--surface)',
        fontFamily: 'var(--font-ui)',
      }}
    >
      <summary
        style={{
          display: 'flex',
          gap: 'var(--space-3)',
          alignItems: 'baseline',
          flexWrap: 'wrap',
          padding: 'var(--space-4)',
          cursor: 'pointer',
          listStyle: 'none',
        }}
      >
        <span
          style={{
            color: 'var(--text-dim)',
            fontFamily: 'var(--font-mono)',
            fontSize: 'var(--font-size-3xs)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          Q{String(question.seq_no).padStart(2, '0')}
        </span>
        {question.section ? (
          <span style={{ color: 'var(--text-dim)', fontSize: 'var(--font-size-3xs)' }}>
            {question.section}
          </span>
        ) : null}
        <span style={{ margin: 0, color: 'var(--text)', fontSize: 'var(--font-size-2xs)', fontWeight: 600, flex: 1 }}>
          {question.text}
        </span>
        {assignee ? (
          <span
            aria-label={`Assigned to ${assignee.display_name}`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 24,
              height: 24,
              borderRadius: '50%',
              background: 'var(--surface-elevated)',
              border: `1px solid ${isMine ? 'var(--accent)' : 'var(--border)'}`,
              color: isMine ? 'var(--accent)' : 'var(--text-muted)',
              fontSize: 'var(--font-size-3xs)',
              fontWeight: 700,
            }}
          >
            {initials(assignee.display_name)}
          </span>
        ) : canAssign ? (
          <span
            aria-label="Unassigned"
            style={{
              padding: '2px var(--space-2)',
              borderRadius: 'var(--radius-input)',
              border: '1px dashed var(--border-strong)',
              color: 'var(--text-dim)',
              fontSize: 'var(--font-size-3xs)',
            }}
          >
            Unassigned
          </span>
        ) : null}
        <ConfidenceBadge confidence={confidence} draftOrigin={question.draft_origin} />
        {needsReview && !isApproved ? <StatusChip tone="neutral">needs review</StatusChip> : null}
        {isApproved ? <StatusChip tone="success">approved</StatusChip> : null}
      </summary>

      <div style={{ padding: '0 var(--space-4) var(--space-4)', display: 'grid', gap: 'var(--space-3)' }}>
        {mode === 'editing' ? (
          <Textarea
            value={draftEdit}
            onChange={(e) => setDraftEdit(e.currentTarget.value)}
            rows={4}
            disabled={busy}
          />
        ) : (
          <p
            style={{
              margin: 0,
              color: displayText ? 'var(--text)' : 'var(--text-muted)',
              fontSize: 'var(--font-size-2xs)',
              fontStyle: displayText ? 'normal' : 'italic',
              lineHeight: 'var(--line-height-body)',
              whiteSpace: 'pre-wrap',
            }}
          >
            {displayText || 'No draft yet — match or draft stage has not run.'}
          </p>
        )}

        {answer?.change_request_reason && needsReview && !isApproved ? (
          <Alert variant="info">Changes requested: {answer.change_request_reason}</Alert>
        ) : null}
        {editedByName && !isApproved ? (
          <p style={{ margin: 0, color: 'var(--text-dim)', fontSize: 'var(--font-size-3xs)' }}>
            Edited by {editedByName}
            {answer?.preparer_edited_at ? ` on ${answer.preparer_edited_at.slice(0, 10)}` : ''}
          </p>
        ) : null}
        {isApproved && answer?.approval_note ? (
          <p style={{ margin: 0, color: 'var(--text-dim)', fontSize: 'var(--font-size-3xs)' }}>
            Rationale: {answer.approval_note}
          </p>
        ) : null}

        {citations.length > 0 ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
            {citations.map((c) => (
              <CitationChip
                key={c.risk_item_id}
                citation={c}
                title={citedTitleById.get(c.risk_item_id) ?? null}
                registerHref={`${registerPrefix}/${encodeURIComponent(c.risk_item_id)}`}
              />
            ))}
          </div>
        ) : null}

        {canAssign ? (
          <AssigneeControl
            questionId={question.question_id}
            assignedToUserId={question.assigned_to_user_id}
            members={members}
            busy={busy}
            onAssign={onAssign}
          />
        ) : null}

        {editable && answer ? (
          <footer style={{ display: 'grid', gap: 'var(--space-2)' }}>
            {mode === 'requesting_changes' ? (
              <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
                <Textarea
                  value={changeReason}
                  onChange={(e) => setChangeReason(e.currentTarget.value)}
                  rows={2}
                  placeholder="Why is this being kicked back? (required)"
                  disabled={busy}
                />
                {actionError ? <Alert variant="error">{actionError}</Alert> : null}
                <div style={{ display: 'flex', gap: 'var(--space-3)', justifyContent: 'flex-end' }}>
                  <Button variant="secondary" compact onClick={cancelEdit} disabled={busy}>
                    Cancel
                  </Button>
                  <Button
                    variant="secondary"
                    compact
                    onClick={submitRequestChanges}
                    disabled={busy || !changeReason.trim()}
                  >
                    Send back for changes
                  </Button>
                </div>
              </div>
            ) : (
              <>
                {(requireApprovalRationale || approvalNoteDraft) && !isApproved ? (
                  <Textarea
                    value={approvalNoteDraft}
                    onChange={(e) => setApprovalNoteDraft(e.currentTarget.value)}
                    rows={2}
                    placeholder={requireApprovalRationale ? 'Approval rationale (required by this workspace)' : 'Approval rationale (optional)'}
                    disabled={busy}
                  />
                ) : null}
                {actionError ? <Alert variant="error">{actionError}</Alert> : null}
                <div style={{ display: 'flex', gap: 'var(--space-3)', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                  {mode === 'editing' ? (
                    <>
                      <Button variant="secondary" compact onClick={cancelEdit} disabled={busy}>
                        Cancel
                      </Button>
                      <Button variant="secondary" compact onClick={saveEdit} disabled={busy || !draftEdit.trim()}>
                        Save
                      </Button>
                    </>
                  ) : !isApproved ? (
                    <>
                      <Button variant="secondary" compact onClick={startEdit} disabled={busy}>
                        Edit
                      </Button>
                      {!requireApprovalRationale && !approvalNoteDraft ? (
                        <Button variant="secondary" compact onClick={() => setApprovalNoteDraft(' ')} disabled={busy}>
                          Add rationale
                        </Button>
                      ) : null}
                      <Button variant="secondary" compact onClick={startRequestChanges} disabled={busy}>
                        Request changes
                      </Button>
                      <Button
                        variant="secondary"
                        compact
                        onClick={submitApprove}
                        disabled={busy || !displayText.trim() || (requireApprovalRationale && !approvalNoteDraft.trim())}
                        title={
                          !displayText.trim()
                            ? 'Add a response before approving.'
                            : requireApprovalRationale && !approvalNoteDraft.trim()
                              ? 'This workspace requires an approval rationale.'
                              : ''
                        }
                      >
                        Approve
                      </Button>
                    </>
                  ) : (
                    <span style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-3xs)' }}>
                      Approved {answer.approved_at ? new Date(answer.approved_at).toISOString().slice(0, 10) : ''}
                      {approvedByName ? ` by ${approvedByName}` : ''}
                    </span>
                  )}
                </div>
              </>
            )}
          </footer>
        ) : null}
      </div>
    </details>
  );
}

// A workaround for the "Add rationale" placeholder trick above: an empty
// string that's still truthy-visible would defeat the `!approvalNoteDraft`
// checks, so the toggle sets a single space rather than introducing a
// separate boolean just to control visibility of an otherwise-optional field.
// (Left as a code comment, not a component — see `setApprovalNoteDraft(' ')` above.)

// ---- Confidence + status chips (PR-P2-4) --------------------------------
//
// The design-system Badge component only has two tones (neutral/accent) —
// not enough to distinguish low from medium confidence, which Priya's
// persona critique flagged as a real risk (the riskiest drafts were
// visually camouflaged as the same grey badge as medium-confidence ones).
// These use the same danger/warning/success token pairs MyWork.tsx's
// Tile component already established for exactly this kind of
// custom-tone-not-available-in-Badge situation.

const ORIGIN_TOOLTIP: Record<string, string> = {
  attested: 'Backed by attested documentation',
  declared: 'Partial evidence — some supporting documents may be stale or incomplete',
  ai_inferred: 'Framework-based response — no uploaded evidence supports this answer yet',
};

function ConfidenceBadge({ confidence, draftOrigin }: { confidence: 'high' | 'medium' | 'low'; draftOrigin?: DraftOrigin | null }) {
  const tone =
    confidence === 'high'
      ? { bg: 'var(--success-bg)', fg: 'var(--success-fg)' }
      : confidence === 'medium'
        ? { bg: 'var(--warning-bg)', fg: 'var(--warning-fg)' }
        : { bg: 'var(--danger-bg)', fg: 'var(--danger-fg)' };
  const tooltip = draftOrigin ? ORIGIN_TOOLTIP[draftOrigin] : undefined;
  return (
    <span
      title={tooltip}
      style={{
        padding: '2px var(--space-2)',
        borderRadius: 'var(--radius-chip)',
        background: tone.bg,
        color: tone.fg,
        fontSize: 'var(--font-size-3xs)',
        fontWeight: 600,
        cursor: tooltip ? 'help' : undefined,
      }}
    >
      {confidence} confidence
    </span>
  );
}

function StatusChip({ tone, children }: { tone: 'neutral' | 'success'; children: React.ReactNode }) {
  const style =
    tone === 'success'
      ? { border: '1px solid var(--success)', color: 'var(--success-fg)', background: 'var(--success-bg)' }
      : { border: '1px solid var(--border-strong)', color: 'var(--text-muted)', background: 'transparent' };
  return (
    <span style={{ ...style, padding: '2px var(--space-2)', borderRadius: 'var(--radius-chip)', fontSize: 'var(--font-size-3xs)', fontWeight: 600 }}>
      {children}
    </span>
  );
}

// ---- Citation chip + popover (PR-P2-4 / mockup B3) ---------------------

function CitationChip({
  citation,
  title,
  registerHref,
}: {
  citation: AnswerCitation;
  title: string | null;
  registerHref: string;
}) {
  const [open, setOpen] = useState(false);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const domId = useMemo(() => `cite-dialog-${citation.risk_item_id}`, [citation.risk_item_id]);

  // A `<dialog>` shown via the `open` attribute alone (rather than
  // `.showModal()`) renders in-flow with no backdrop, no focus trap, and
  // no Escape-to-close — the aesthetic and flow critics both caught this
  // independently. showModal()/close() restore all three for free.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  return (
    <>
      <button
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={domId}
        onClick={() => setOpen(true)}
        style={{
          padding: 'var(--space-1) var(--space-2)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-input)',
          background: 'var(--surface-elevated)',
          color: 'var(--text-muted)',
          fontFamily: 'var(--font-mono)',
          fontSize: 'var(--font-size-3xs)',
          fontVariantNumeric: 'tabular-nums',
          cursor: 'pointer',
        }}
      >
        § {title ?? citation.risk_item_id} · {(citation.relevance * 100).toFixed(0)}%
      </button>
      <dialog
        ref={dialogRef}
        id={domId}
        aria-label={title ? `Citation — ${title}` : `Citation ${citation.risk_item_id}`}
        onClose={() => setOpen(false)}
        onCancel={() => setOpen(false)}
        style={{
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-card)',
          background: 'var(--surface-elevated)',
          color: 'var(--text)',
          padding: 'var(--space-4)',
          maxWidth: '28rem',
          fontFamily: 'var(--font-ui)',
          display: 'grid',
          gap: 'var(--space-2)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--space-3)' }}>
          <strong style={{ fontSize: 'var(--font-size-2xs)' }}>{title ?? citation.risk_item_id}</strong>
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Close"
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--text-muted)',
              cursor: 'pointer',
              fontSize: 'var(--font-size-sm)',
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>
        {title ? (
          <p style={{ margin: 0, color: 'var(--text-dim)', fontSize: 'var(--font-size-3xs)', fontFamily: 'var(--font-mono)' }}>
            {citation.risk_item_id}
          </p>
        ) : null}
        <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: 'var(--font-size-3xs)' }}>
          Relevance {(citation.relevance * 100).toFixed(0)}% — {citation.why}
        </p>
        <Link to={registerHref} onClick={() => setOpen(false)} style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--accent)' }}>
          Open register item →
        </Link>
      </dialog>
    </>
  );
}

// ---- Assignee control (PR-P2-4) -----------------------------------------

function AssigneeControl({
  questionId,
  assignedToUserId,
  members,
  busy,
  onAssign,
}: {
  questionId: string;
  assignedToUserId: string | null;
  members: WorkspaceMember[] | null;
  busy: boolean;
  onAssign: (questionId: string, assigneeUserId: string | null) => Promise<ActionResult>;
}) {
  return (
    <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap' }}>
      <label htmlFor={`assign-${questionId}`} style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-dim)' }}>
        Assigned to
      </label>
      <select
        id={`assign-${questionId}`}
        value={assignedToUserId ?? ''}
        onChange={(e) => onAssign(questionId, e.target.value || null)}
        disabled={busy || members == null}
        style={{
          padding: 'var(--space-1) var(--space-2)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-input)',
          fontSize: 'var(--font-size-3xs)',
          background: 'var(--surface)',
          color: 'var(--text)',
        }}
      >
        <option value="">— Unassigned —</option>
        {(members ?? []).map((m) => (
          <option key={m.user_id} value={m.user_id}>
            {m.display_name}
          </option>
        ))}
      </select>
    </div>
  );
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function safeName(title: string, id: string): string {
  const base = title.replace(/[^\w.\-]+/g, '_').slice(0, 80) || id;
  return `${base}-${id.slice(-8)}`;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

function parseCitations(raw: string): AnswerCitation[] {
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as AnswerCitation[]) : [];
  } catch {
    return [];
  }
}

// ---- Evidence readiness summary (evidence-plan §3.2) ---------------------

const ORIGIN_LABEL: Record<DraftOrigin, string> = {
  attested: 'Evidence-backed',
  declared: 'Partial',
  ai_inferred: 'Aspirational',
};

const ORIGIN_COLOR: Record<DraftOrigin, string> = {
  attested: 'var(--success-fg)',
  declared: 'var(--warning-fg)',
  ai_inferred: 'var(--text-muted)',
};

function EvidenceReadinessSummary({ questions }: { questions: QuestionRow[] }) {
  if (questions.length === 0) return null;

  const counts = { attested: 0, declared: 0, ai_inferred: 0, unknown: 0 };
  for (const q of questions) {
    if (q.draft_origin && q.draft_origin in counts) {
      counts[q.draft_origin as DraftOrigin]++;
    } else {
      counts.unknown++;
    }
  }
  const total = questions.length;
  const coveragePct = total > 0 ? Math.round((counts.attested / total) * 100) : 0;

  if (counts.attested === 0 && counts.declared === 0 && counts.unknown === total) return null;

  const segments: Array<{ label: string; count: number; color: string }> = [];
  if (counts.attested > 0) segments.push({ label: ORIGIN_LABEL.attested, count: counts.attested, color: ORIGIN_COLOR.attested });
  if (counts.declared > 0) segments.push({ label: ORIGIN_LABEL.declared, count: counts.declared, color: ORIGIN_COLOR.declared });
  if (counts.ai_inferred > 0) segments.push({ label: ORIGIN_LABEL.ai_inferred, count: counts.ai_inferred, color: ORIGIN_COLOR.ai_inferred });
  if (counts.unknown > 0) segments.push({ label: 'No match', count: counts.unknown, color: 'var(--danger-fg)' });

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 'var(--space-3)',
        alignItems: 'center',
        padding: 'var(--space-2) var(--space-3)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--border)',
        background: 'var(--surface)',
        fontFamily: 'var(--font-ui)',
        fontSize: 'var(--font-size-3xs)',
        color: 'var(--text-muted)',
      }}
    >
      {segments.map((s) => (
        <span key={s.label}>
          <span style={{ color: s.color, fontWeight: 600 }}>{s.label}:</span>{' '}
          {s.count}/{total}
        </span>
      ))}
      {counts.attested > 0 ? (
        <span style={{ color: 'var(--text-dim)' }}>({coveragePct}% evidence coverage)</span>
      ) : null}
    </div>
  );
}
